import { Button } from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { PostCardErrorNotice } from '~publish/components/PostCard/PostCardErrorNotice'
import { PostCardNotice } from '~publish/components/PostCard/PostCardNotice'
import { useReconnectChannel } from '~publish/hooks/useReconnectChannel'

import type { JSX } from 'react'

const DISCONNECTED_MESSAGE = "We've lost access to post this on your behalf."
const CONTACT_ADMIN_MESSAGE =
  'Please contact the administrator of your organization to refresh the connection.'

/**
 * Dialog-only error notice. Unlike the queue, the post details dialog has no
 * page-level "channel disconnected" banner, so a disconnected channel offers
 * no way to reconnect from here. This notice routes on `channel.isDisconnected`:
 * when the channel is the problem it surfaces a Refresh Connection action (using
 * the post's own error message when present), and otherwise defers to the shared
 * `PostCardErrorNotice` for the normal Learn More / reconnected-message behaviour.
 *
 * The shared `PostCardErrorNotice` keeps its Learn More action because in the
 * queue the reconnect affordance lives at the page/channel level; this component
 * is deliberately scoped to the dialog and is not used by the post cards.
 */
export const PostDetailsConnectionNotice = ({
  className,
}: {
  className?: string
}): JSX.Element | null => {
  const { status, error, channel } = usePostData()
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const reconnectChannel = useReconnectChannel()

  const channelNeedsReconnect =
    channel.isDisconnected && status !== 'sent' && status !== 'sending'

  if (!channelNeedsReconnect) {
    return <PostCardErrorNotice className={className} />
  }

  const handleRefresh = (): void => {
    reconnectChannel({
      id: channel.id,
      service: channel.service,
      cta: 'publish-postDetails-connectionNotice-refreshConnection-1',
    })
  }

  const message = error?.message ?? DISCONNECTED_MESSAGE

  return (
    <PostCardNotice variant="critical" className={className}>
      {canManageChannels ? message : `${message} ${CONTACT_ADMIN_MESSAGE}`}
      {canManageChannels && (
        <PostCardNotice.Action>
          <Button variant="critical" size="small" onClick={handleRefresh}>
            Refresh Connection
          </Button>
        </PostCardNotice.Action>
      )}
    </PostCardNotice>
  )
}

PostDetailsConnectionNotice.displayName = 'PostDetailsConnectionNotice'
